import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Row, Col } from "react-bootstrap";
import { FaThumbsUp } from "react-icons/fa6";
import axios from 'axios';
import JoinTeamModal from './JoinTeamModal';
import AddTeamModal from '../teams/AddTeamModal';

function ProfileFeed({ user, activeLeague, activeSeason }) {
	const [games, setGames] = useState([]);
	const [userLeagues, setUserLeagues] = useState([]);
	const [activeLeagues, setActiveLeagues] = useState([]);
	const [teams, setTeams] = useState([]);
	const [showJoinTeamModal, setShowJoinTeamModal] = useState(false);
	const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
	const [justAddedTeam, setJustAddedTeam] = useState(false);

	// Check if user has any players in the current season
	const hasPlayersInCurrentSeason = user?.players?.some(player =>
		player.league_id === activeLeague?.id
	);


	// Check if user's player is in a team for the current league
	const isPlayerInTeam = user?.players?.some(player =>
		player.league_id === activeLeague?.id && player.team_id !== null
	);

	const inTeam = isPlayerInTeam || justAddedTeam;


	useEffect(() => {
		// Fetch user's leagues
		axios.get('/api/leagues/user-leagues/', {
			withCredentials: true,
		})
			.then(response => {
				setUserLeagues(response.data);
			})
			.catch(error => {
				console.error('Error fetching user leagues:', error);
			});

		// Fetch active leagues for registration
		axios.get('/api/leagues/active-leagues/', {
			withCredentials: true,
		})
			.then(response => {
				setActiveLeagues(response.data);
			})
			.catch(error => {
				console.error('Error fetching active leagues:', error);
			});
	}, []);

	useEffect(() => {
		// Fetch teams for the active league
		if (activeLeague) {
			axios.get(`/api/teams/league/${activeLeague.id}/teams/`, {
				withCredentials: true,
			})
				.then(response => {
					setTeams(response.data);
				})
				.catch(error => {
					console.error('Error fetching teams:', error);
				});
		} else {
			setTeams([]); // Clear teams if no active league
		}

		if (activeLeague && inTeam && userLeagues.length > 0) {
			// Fetch games for the active league
			axios.get(`/api/games/upcoming/`, {
				params: { is_played: false },
				withCredentials: true,
			})
				.then(response => {
					setGames(response.data);
				})
				.catch(error => {
					console.error('Error fetching games:', error);
				});
		} else {
			setGames([]); // Clear games if user has no leagues
		}
	}, [activeLeague, inTeam, userLeagues]);

	const handleAttendingToggle = async (gameId, participantId, currentAttending) => {
		try {
			const response = await axios.patch(
				`/api/games/${gameId}/attending/`,
				{ attending: !currentAttending },
				{
					withCredentials: true,
				}
			);

			if (response.status === 200) {
				// Update local state
				setGames((prevGames) =>
					prevGames.map((game) =>
						game.id === gameId
							? {
								...game,
								participants: game.participants.map((p) =>
									p.id === participantId ? { ...p, attending: !currentAttending } : p
								),
							}
							: game
					)
				);
			}
		} catch (error) {
			console.error("Error updating attending status:", error);
		}
	};

	const handleJoinTeamSuccess = () => {
		// Refresh the teams list
		if (activeLeague) {
			axios.get(`/api/teams/league/${activeLeague.id}/teams/`, {
				withCredentials: true,
			})
				.then(response => {
					setTeams(response.data);
				})
				.catch(error => {
					console.error('Error fetching teams:', error);
				});
		}
	};

	const handleTeamAdded = (newTeam) => {
		// hide the modal
		setShowCreateTeamModal(false);
		// mark that we’re in a team now
		setJustAddedTeam(true);
		// refresh the list too, if you want to show it somewhere
		if (activeLeague) {
			axios.get(`/api/teams/league/${activeLeague.id}/teams/`, {
				withCredentials: true,
			}).then(r => setTeams(r.data))
				.catch(e => console.error('Error fetching teams:', e));
		}
	};

	return (
		<div className="p-0">
			{/* Sign Up Card - Show only if user has no players and there are active leagues */}
			{!hasPlayersInCurrentSeason && activeLeagues.length > 0 && (
				<div className="col-12">
					<div className="card mb-3">
						<div className="card-body">
							<h5 className="card-title">Ready to Play?</h5>
							<p className="card-text">
								{activeLeagues.length === 1
									? "There's a league accepting registrations right now!"
									: `There are ${activeLeagues.length} leagues accepting registrations right now!`}
							</p>
							<a
								href={`/league/${activeLeagues[0].id}/register/`}
								className="btn btn-primary"
							>
								Sign Up Now
							</a>
						</div>
					</div>
				</div>
			)}

			{/* Join Team Card - Show only if user has a player but no team */}
			{hasPlayersInCurrentSeason && !inTeam && (
				<div className="col-12">
					<div className="card mb-3">
						<div className="card-body text-center">
							<h5 className="card-title">Join a Team</h5>

							{teams.length > 0 ? (
								<p className="card-text">
									{teams.length === 1
										? "There's one team looking for players!"
										: `There are ${teams.length} teams looking for players!`}
								</p>
							) : (
								<p className="card-text">
									No teams have been created for this season yet.
								</p>
							)}

							{/* Actions */}
							<Row className="justify-content-center align-items-center mt-3">
								{teams.length > 0 && (
									<>
										<Col xs="auto">
											<Button
												variant="primary"
												size="sm"
												onClick={() => setShowJoinTeamModal(true)}
											>
												View Teams
											</Button>
										</Col>

										<Col xs="auto">
											<span className="fw-semibold mx-3">or</span>
										</Col>
									</>
								)}

								<Col xs="auto">
									<Button
										variant="secondary"
										size="sm"
										onClick={() => setShowCreateTeamModal(true)}
									>
										Create a Team
									</Button>
								</Col>
							</Row>
						</div>
					</div>
				</div>
			)}

			{/* Join Team Modal */}
			<JoinTeamModal
				isOpen={showJoinTeamModal}
				onClose={(success) => {
					setShowJoinTeamModal(false);
					if (success) {
						handleJoinTeamSuccess();
					}
				}}
				leagueId={activeLeague?.id}
			/>

			{/* Rest of your feed content */}
			<div className="col-12">
				{/* Events START */}
				<div className="card">
					<div className="card-header d-sm-flex align-items-center justify-content-between border-0 pb-0">
						<h5 className="card-title mb-sm-0">Upcoming Games</h5>
					</div>
					{/* <div className="card-body pb-0">
						<div className="alert alert-success alert-dismissible fade show" role="alert">
							<strong>Announcement:</strong> Weather all clear: Games still scheduled.
							<button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
						</div>
					</div> */}
					<div className="card-body pt-1">
						{games.length > 0 ? (
							games.map((game) => {
								const homeParticipant = game.participants?.find((p) => p.is_home && p.content_type === 'team') || {};
								const awayParticipant = game.participants?.find((p) => p.is_away && p.content_type === 'team') || {};
								const userParticipant = game.participants?.find(
									(p) => p.content_type === "player" && user?.players?.some(player => player.id === p.object_id)
								) || {};

								return (
									<div key={game.id} className="row mb-3">
										<div className="col-9">
											<div className="d-sm-flex align-items-center">
												<div className="avatar avatar-xl">
													<div className="position-relative">
														<div
															style={{
																width: '50px',
																height: '50px',
																margin: 'auto',
																display: 'block',
																position: 'relative',
																overflow: 'hidden',
																borderRadius: '8px',
															}}
														>
															{homeParticipant.participant_logo ? (
																<img
																	src={homeParticipant.participant_logo}
																	alt={`${homeParticipant.participant_name} logo`}
																	style={{
																		width: '50px',
																		height: '50px',
																		objectFit: 'cover',
																		position: 'absolute',
																		left: 0,
																		top: 0,
																		clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)',
																	}}
																/>
															) : (
																<div
																	style={{
																		width: '100%',
																		height: '50%',
																		backgroundColor: 'blue',
																		position: 'absolute',
																		top: 0,
																	}}
																></div>
															)}

															{awayParticipant.participant_logo ? (
																<img
																	src={awayParticipant.participant_logo}
																	alt={`${awayParticipant.participant_name} logo`}
																	style={{
																		width: '50px',
																		height: '50px',
																		objectFit: 'cover',
																		position: 'absolute',
																		left: 0,
																		bottom: 0,
																		clipPath: 'polygon(0 50%, 100% 50%, 100% 100%, 0 100%)',
																	}}
																/>
															) : (
																<div
																	style={{
																		width: '100%',
																		height: '50%',
																		backgroundColor: 'red',
																		position: 'absolute',
																		bottom: 0,
																	}}
																></div>
															)}
														</div>
													</div>
												</div>
												<div className="ms-sm-4 mt-2 mt-sm-0">
													<h5 className="mb-1">
														<Link className="nav-link" to={`/games/${game.id}`}>
															{game.home_team} vs. {game.away_team}
														</Link>
													</h5>
													<ul className="nav nav-stack small">
														<li className="nav-item">
															<i className="bi bi-calendar-check pe-1"></i> {game.scheduled_date_only} at {game.scheduled_time_only}
														</li>
														<li className="nav-item">
															<i className="bi bi-geo-alt pe-1"></i> {game.location.name}
														</li>
													</ul>
												</div>
											</div>
										</div>
										<div className="col-3">
											<div className="ms-sm-auto mt-2 mt-sm-0">
												<input
													type="checkbox"
													className="btn-check"
													id={`Interested-${userParticipant.id}`}
													checked={userParticipant.attending || false}
													onChange={() => handleAttendingToggle(game.id, userParticipant.id, userParticipant.attending)}
												/>
												<label
													className={`btn btn-sm ${userParticipant.attending ? 'btn-success' : 'btn-outline-success'}`}
													htmlFor={`Interested-${userParticipant.id}`}
												>
													<FaThumbsUp className="me-1" /> {userParticipant.attending ? "Attending" : "Not Attending"}
												</label>
											</div>
										</div>
									</div>
								);
							})
						) : (
							<p>No upcoming games found.</p>
						)}
					</div>
				</div>
				{/* Events END */}
			</div>

			{/* Add Team Modal */}
			<AddTeamModal
				show={showCreateTeamModal}
				handleClose={() => setShowCreateTeamModal(false)}
				onTeamAdded={handleTeamAdded}
			/>
		</div>
	);
}

export default ProfileFeed;
